import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { trackEvent, getAttribution } from "@/lib/analytics";

const COVERAGE = ["Full wrap — maximum impact", "Partial wrap — sides + rear", "Logos & lettering only", "Not sure — recommend for me"];
const VEHICLE_COUNT = ["Just one", "2–3 vehicles", "4–6 vehicles", "7+ vehicles"];
const BUDGET = ["Under $1,500", "$1,500 – $3,500", "$3,500 – $7,500", "$7,500+", "Not sure yet"];

/**
 * Self-contained commercial wraps 3-step quote form (coverage → budget ack → contact).
 * Used by the Meta ads LP (/lp/commercial-wraps). No Toaster here — host page provides it.
 * `bare` drops the outer card chrome for embedding.
 */
export default function CommercialWrapsForm({ source, lpKey, testidPrefix, bare = false, projectType = "Commercial Vehicle Wrap" }) {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", business_name: "", vehicle: "",
    coverage: "", vehicle_count: "", budget: "",
  });
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const STEPS = ["Your project", "Budget", "Contact"];

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canSubmit =
    form.full_name.trim() && form.email.trim() && form.phone.trim() &&
    form.coverage && form.vehicle_count && form.budget;

  const step0Ready = form.coverage && form.vehicle_count;
  const step1Ready = Boolean(form.budget);
  const step2Ready = form.full_name.trim() && form.email.trim() && form.phone.trim();

  const next = () => {
    const nextStep = step + 1;
    trackEvent("lp_form_step_view", { lp: lpKey, step: nextStep + 1, step_name: STEPS[nextStep] });
    setStep(nextStep);
  };
  const back = () => setStep(Math.max(0, step - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    try {
      const details = [
        form.business_name.trim() && `Business\n  → ${form.business_name.trim()}`,
        form.vehicle.trim() && `Vehicle year / make / model\n  → ${form.vehicle.trim()}`,
        `How much coverage?\n  → ${form.coverage}`,
        `How many vehicles?\n  → ${form.vehicle_count}`,
        `Budget\n  → ${form.budget}`,
      ].filter(Boolean).join("\n\n");

      await api.post("/leads", {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        business_name: form.business_name.trim() || undefined,
        vehicle_type: form.vehicle.trim() || undefined,
        project_type: projectType,
        project_details: details,
        referral_source: source,
        ...getAttribution(),
      });
      toast.success("Mockup request received!");
      trackEvent("lp_form_submit", { lp: lpKey });
      const submissionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      navigate("/mockup/thank-you", {
        state: {
          fromSubmit: true,
          submissionId,
          service: projectType,
          vehicle: form.vehicle.trim() || "n/a",
          has_business: Boolean(form.business_name.trim()),
          source,
        },
        replace: true,
      });
    } catch (err) {
      trackEvent("lp_form_error", { lp: lpKey, message: formatApiErrorDetail(err.response?.data?.detail) });
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={`${testidPrefix}-form`}
      className={bare ? "" : "bg-white rounded-3xl border border-neutral-200 p-6 md:p-8 shadow-xl"}
    >
      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1 flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold border transition-colors ${i <= step ? "bg-[var(--red)] text-white border-[var(--red)]" : "bg-white text-neutral-500 border-neutral-200"}`}>
              {i < step ? <Check size={12}/> : i + 1}
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-[2px] bg-neutral-200 overflow-hidden">
                <div className="h-full bg-[var(--red)] transition-all duration-300" style={{ width: i < step ? "100%" : "0" }}/>
              </div>
            )}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* STEP 1 — Coverage + vehicle count (low-friction taps, no typing) */}
        {step === 0 && (
          <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <div className="eyebrow eyebrow-red">Step 1 of 3</div>
            <h3 className="font-display text-2xl mt-2 leading-tight">How much of it should we wrap?</h3>
            <p className="text-sm text-neutral-500 mt-1">So Dave can design your free mockup around it.</p>

            <div className="mt-5 space-y-2">
              {COVERAGE.map((c) => {
                const active = form.coverage === c;
                return (
                  <button
                    key={c}
                    type="button"
                    data-testid={`${testidPrefix}-coverage-${active ? "active" : "opt"}-${c.slice(0,8).replace(/[^a-z]/gi,"").toLowerCase()}`}
                    onClick={() => setForm({ ...form, coverage: c })}
                    className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all ${active ? "border-[var(--red)] bg-red-50/60" : "border-neutral-200 hover:border-neutral-400"}`}
                  >
                    <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 grid place-items-center ${active ? "border-[var(--red)] bg-[var(--red)]" : "border-neutral-300"}`}>
                      {active && <span className="w-2 h-2 rounded-full bg-white" />}
                    </span>
                    <span className={`text-sm ${active ? "text-neutral-900 font-semibold" : "text-neutral-700"}`}>{c}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6">
              <label className="eyebrow">How many vehicles?</label>
              <select data-testid={`${testidPrefix}-vehicle-count`} required className="input-928 mt-2" value={form.vehicle_count} onChange={set("vehicle_count")}>
                <option value="">Select…</option>
                {VEHICLE_COUNT.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>

            <button
              type="button"
              data-testid={`${testidPrefix}-next-1`}
              disabled={!step0Ready}
              onClick={next}
              className={`btn-red w-full justify-center mt-6 ${!step0Ready ? "opacity-60 pointer-events-none" : ""}`}
            >
              Continue <ArrowRight size={16}/>
            </button>
          </motion.div>
        )}

        {/* STEP 2 — Budget range */}
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <div className="eyebrow eyebrow-red">Step 2 of 3</div>
            <h3 className="font-display text-2xl mt-2 leading-tight">What budget are you working with?</h3>
            <p className="text-sm text-neutral-500 mt-1">A rough range is perfect — Dave tailors your mockup to it.</p>

            <div data-testid={`${testidPrefix}-budget`} className="mt-5 space-y-2">
              {BUDGET.map((b, i) => {
                const active = form.budget === b;
                return (
                  <button
                    key={b}
                    type="button"
                    data-testid={`${testidPrefix}-budget-${active ? "active" : "opt"}-${i}`}
                    onClick={() => setForm({ ...form, budget: b })}
                    className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all ${active ? "border-[var(--red)] bg-red-50/60" : "border-neutral-200 hover:border-neutral-400"}`}
                  >
                    <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 grid place-items-center ${active ? "border-[var(--red)] bg-[var(--red)]" : "border-neutral-300"}`}>
                      {active && <span className="w-2 h-2 rounded-full bg-white" />}
                    </span>
                    <span className={`text-sm ${active ? "text-neutral-900 font-semibold" : "text-neutral-700"}`}>{b}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <button type="button" data-testid={`${testidPrefix}-back-1`} onClick={back} className="btn-ghost"><ArrowLeft size={16}/> Back</button>
              <button
                type="button"
                data-testid={`${testidPrefix}-next-2`}
                disabled={!step1Ready}
                onClick={next}
                className={`btn-red ${!step1Ready ? "opacity-60 pointer-events-none" : ""}`}
              >
                Continue <ArrowRight size={16}/>
              </button>
            </div>
          </motion.div>
        )}

        {/* STEP 3 — Contact + business + vehicle (the "closer") */}
        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <div className="eyebrow eyebrow-red">Step 3 of 3 · Last step</div>
            <h3 className="font-display text-2xl mt-2 leading-tight">Where should Dave send the mockup?</h3>
            <p className="text-sm text-neutral-500 mt-1">He&apos;ll call within 1 business day.</p>

            <div className="mt-5 space-y-3">
              <input data-testid={`${testidPrefix}-name`} required placeholder="Full name *" className="input-928" value={form.full_name} onChange={set("full_name")} />
              <input data-testid={`${testidPrefix}-email`} required type="email" placeholder="Email *" className="input-928" value={form.email} onChange={set("email")} />
              <input data-testid={`${testidPrefix}-phone-input`} required type="tel" placeholder="Phone *" className="input-928" value={form.phone} onChange={set("phone")} />
              <input data-testid={`${testidPrefix}-business`} placeholder="Business name" className="input-928" value={form.business_name} onChange={set("business_name")} />
              <input data-testid={`${testidPrefix}-vehicle`} placeholder="Vehicle year / make / model (e.g. 2020 Ford Transit)" className="input-928" value={form.vehicle} onChange={set("vehicle")} />
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <button type="button" data-testid={`${testidPrefix}-back-2`} onClick={back} className="btn-ghost"><ArrowLeft size={16}/> Back</button>
              <button
                type="submit"
                data-testid={`${testidPrefix}-submit`}
                disabled={loading || !canSubmit || !step2Ready}
                className={`btn-red flex-1 justify-center whitespace-nowrap ${loading || !canSubmit || !step2Ready ? "opacity-60 pointer-events-none" : ""}`}
              >
                {loading ? "Sending…" : "Get My FREE Mockup"}
                <ArrowRight size={16}/>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="mt-6 text-xs text-neutral-500 text-center">We never share your info. Ever. · Dave replies within 1 business day.</p>
    </form>
  );
}
